import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api/Api";
import { ArrowLeft, Plus, Trash2, CheckCircle, Layers, Save } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css"; 

const FeedbackSubcategoryAddPage = () => {
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [rows, setRows] = useState([{ subcategory_name: "" }]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/adminmod/feedback-category-dropdown").then((res) => {
      setCategories(res.data.data || []);
    });
  }, []);

  const handleChange = (index, value) => {
    const updated = [...rows];
    updated[index].subcategory_name = value;
    setRows(updated);
  };

  const addMore = () => {
    if (rows.length >= 10) {
      toast.error("Maximum 10 subcategories allowed");
      return;
    }
    setRows([...rows, { subcategory_name: "" }]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) {
      toast.error("At least one row is required");
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!categoryId) {
      toast.error("Please select a category");
      return;
    }

    for (let row of rows) {
      if (!row.subcategory_name.trim()) {
        toast.error("Sub category name required for all rows");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await api.post("/adminmod/feedback-subcategory-add", {
        category_id: categoryId,
        items: rows,
      });

      if (res.data.success) {
        toast.success("Sub categories added successfully");
        navigate("/master/feedback-subcategory");
      }
    } catch (error) {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      
      {/* HEADER CARD - Full Width */}
      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">
        <div className="card-body d-flex align-items-center justify-content-between p-3">
          <div>
            <h4 className="mb-1 fw-bold text-dark">Add Feedback Sub Category</h4>
            <p className="text-muted small mb-0">Create new sub-categories under a main category</p>
          </div>
          
          <Link
            to="/master/feedback-subcategory"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} /> Back to List
          </Link>
        </div>
      </div>

      {/* MAIN FORM CARD - Full Width */}
      <div className="card shadow-sm border-0 rounded-3">
        <div className="card-body p-4">
          
          {/* 1. CATEGORY SELECTION (Restricted width for better look) */}
          <div className="row mb-4">
            <div className="col-12 col-md-6 col-lg-4">
              <label className="form-label fw-bold text-secondary small text-uppercase">
                Select Main Category
              </label>
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <Layers size={18} className="text-muted" />
                </span>
                <select
                  className="form-select border-start-0 ps-0 focus-ring-none"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  style={{ height: "45px" }}
                >
                  <option value="">-- Select Feedback Category --</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.item_names}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between mb-3">
            <label className="form-label fw-bold text-secondary small text-uppercase mb-0">
              Add Sub Categories
            </label>
            <span className="badge bg-light text-secondary border">
              {rows.length} Items
            </span>
          </div>
          
          {/* 2. DYNAMIC TABLE - Full Width */}
          <div className="table-responsive mb-0 border rounded-3">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="text-center py-3 text-secondary small fw-bold" style={{ width: "5%" }}>#</th>
                  <th className="py-3 text-secondary small fw-bold" style={{ width: "80%" }}>Sub Category Name</th>
                  <th className="py-3 text-center text-secondary small fw-bold" style={{ width: "15%" }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td className="text-center text-muted fw-bold">{index + 1}</td>
                    <td>
                      <input
                        type="text"
                        className="form-control border-0 bg-transparent p-0"
                        style={{ boxShadow: 'none' }}
                        placeholder="e.g. Cleanliness, Behavior..."
                        value={row.subcategory_name}
                        onChange={(e) => handleChange(index, e.target.value)}
                        autoFocus={index === rows.length - 1 && index > 0}
                      />
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-light text-danger btn-sm border-0 hover-shadow"
                        onClick={() => removeRow(index)}
                        disabled={rows.length === 1}
                        title="Remove"
                        style={{ width: "32px", height: "32px", borderRadius: "50%" }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>

        {/* FOOTER ACTIONS */}
        <div className="card-footer bg-white p-3 d-flex justify-content-between align-items-center">
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 border-dashed px-3"
            onClick={addMore}
          >
            <Plus size={16} /> Add Another Row
          </button>

          <button
            className="btn btn-success d-flex align-items-center gap-2 px-4 fw-medium"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <div className="spinner-border spinner-border-sm text-white" role="status"></div>
            ) : (
              <CheckCircle size={18} />
            )}
            Save Sub Categories
          </button>
        </div>

      </div>
    </div>
  );
};

export default FeedbackSubcategoryAddPage;